import { IContextualMenuItem } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { useLazyUnpublishItemQuery } from "../../../src/taskpane/components/_v2/redux/api/sharepoint/api-sharepoint-slice";
import { SharePointFolderContentTypeIds } from "../../../src/taskpane/config/constants";
import { RootState } from "../../../src/taskpane/redux";
import authModule from "../../../utilities/AuthModule";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { logger } from "../../../utilities/logger";
import { menuItemIconStyles } from "../../../src/taskpane/config/controlStyles";
import { StackOff20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/StackOff20Regular";

export function useMenuItem_Unpublish(
    selectedItems: any[],
    waitFor: boolean,
    setWaitFor: (waitFor: boolean) => void,
    isInOneDrive: boolean,
    setGridItemIsBusy: (items: any[], isBusy: boolean) => void,
    updateGridItems: (items: any[]) => void,
    setIsCMErrorModalOpen: (isCMErrorModalOpen: boolean) => void,
    setErrorMessage?: (errorMessage: string) => void
) {
    const languageStrings = React.useContext(LanguageContext);

    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);

    const [unpublishItem] = useLazyUnpublishItemQuery();

    const onUnpublishItem = React.useCallback(async (item: any) => {
        setWaitFor(true);
        setGridItemIsBusy([item], true);

        try {
            await unpublishItem({
                siteUrl: currentDirectory.webUrl,
                uniqueId: item.UniqueId.replace("{", "").replace("}", ""),
                accessToken: await authModule.getSharePointAccessToken(),
            }).unwrap();
        } catch (error) {
            logger(error, "useMenuItem_Unpublish (onUnpublishItem)");
            setIsCMErrorModalOpen(true);
            setErrorMessage(error['data']['odata.error']['message']['value']);
        } finally {
            setWaitFor(false);
            updateGridItems([item]);
        }
    }, [currentDirectory]);

    const menuItem_Unpublish = React.useMemo(() => {
        const hasSingleSelection = selectedItems.length === 1;
        if (isInOneDrive || !hasSingleSelection) {
            return null;
        }

        const selectedItem = selectedItems[0];

        const isPending = selectedItem['_ModerationStatus.'] === '2';
        const isNotCheckedOut = !selectedItem.CheckoutUser;

        const canPerformAction = (isNotCheckedOut || isPending) && currentSharepointFolder?.userPermissions?.canEditListItem;

        const isNotFolder = !selectedItem.ContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder);

        if (canPerformAction && isNotFolder) {
            const majorVersion = Math.floor(+selectedItem._UIVersion / 512);
            const minorVersion = +selectedItem._UIVersion % 512;

            // Item is either a major published version or is pending approval
            const isUnpublishableState = (majorVersion > 0 && minorVersion === 0) || isPending;

            if (isUnpublishableState) {
                const text = isPending ? languageStrings.CancelApproval : languageStrings.Unpublish;
                return {
                    key: 'unpublish',
                    disabled: waitFor === true,
                    text: text,
                    // iconProps: { iconName: "UnpublishContent" },
                    iconProps: {},
                    onRenderIcon: () => <StackOff20Regular className={menuItemIconStyles} />,
                    onClick: () => { onUnpublishItem(selectedItem) },
                } as IContextualMenuItem;
            }
        }

        return null;
    }, [selectedItems, waitFor, isInOneDrive, currentSharepointFolder, onUnpublishItem]);

    return {
        menuItem_Unpublish
    }
}